import React, { useCallback, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box } from '@hopskipdrive/react-components';

import type { Recording } from '../../api/services/Recordings';
import { useTimezone } from '../../hooks/useTimezone';
import { ActionBar } from '../../shared/components/ActionBar/ActionBar';
import { Text } from '../../shared/components/Text';
import { DownloadRecordingModal } from '../features/DownloadRecordingModal';
import { RecordingsFilterBar } from '../features/RecordingsFilterBar';
import { RecordingsTable } from '../features/RecordingsTable';
import { DEFAULT_RECORDINGS_PARAMS, RecordingsQueryParams } from '../hooks/useRecordingsQuery';
import { rehydrateRecordingsDateRange } from '../utils/dateRange';

const getInitialParams = (
  searchParams: URLSearchParams,
  getDateInTimezone: (date?: Date | string) => Date,
  getStartOfDate: (date: Date) => Date,
  getEndOfDate: (date: Date) => Date
): RecordingsQueryParams => {
  const fromDate = searchParams.get('fromDate');
  const { startDate, endDate } = fromDate
    ? rehydrateRecordingsDateRange(fromDate, getDateInTimezone, getStartOfDate, getEndOfDate)
    : { startDate: undefined, endDate: undefined };

  return {
    ...DEFAULT_RECORDINGS_PARAMS,
    riderName: searchParams.get('riderName') ?? undefined,
    startDate,
    endDate,
  };
};

const RecordingsPage: React.FC = () => {
  const { getDateInTimezone, getStartOfDate, getEndOfDate } = useTimezone();
  const [searchParams, setSearchParams] = useSearchParams();
  const [queryParams, setQueryParams] = useState<RecordingsQueryParams>(() =>
    getInitialParams(searchParams, getDateInTimezone, getStartOfDate, getEndOfDate)
  );
  const [selectedRecording, setSelectedRecording] = useState<Recording | null>(null);

  const updateFilters = useCallback((filters: Partial<RecordingsQueryParams>) => {
    setQueryParams((prev) => ({ ...prev, ...filters, page: 1 }));
  }, []);

  const handlePageChange = useCallback((params: { page: number; perPage: number }) => {
    setQueryParams((prev) => ({ ...prev, ...params }));
  }, []);

  const handleResetFilters = useCallback(() => {
    setQueryParams(DEFAULT_RECORDINGS_PARAMS);
    setSearchParams({});
  }, [setSearchParams]);

  return (
    <>
      <ActionBar>
        <ActionBar.Title
          title={
            <Box display="flex" flexDirection="column" gap="10px">
              <Text variant="h1" fontWeight={700}>
                Ride Recordings
              </Text>
              <Text variant="body2" textColor="secondaryText" fontWeight={400}>
                Recordings are stored for 30 days to optimize storage and comply with data retention policies.
              </Text>
            </Box>
          }
        />
      </ActionBar>
      <Box pb="12px">
        <RecordingsFilterBar queryParams={queryParams} onFilterChange={updateFilters} />
      </Box>
      <Box>
        <RecordingsTable
          queryParams={queryParams}
          onPageChange={handlePageChange}
          onResetFilters={handleResetFilters}
          onDownload={(recording) => setSelectedRecording(recording)}
        />
      </Box>
      <DownloadRecordingModal recording={selectedRecording} onClose={() => setSelectedRecording(null)} />
    </>
  );
};

export default RecordingsPage;
